import { isEmpty } from 'lodash-es';
import { getBsActiveBonus, getCrmWagerBonuses } from '@namespace/bonuses';
import { SLUG_PARAMETER } from '../constants';
import {
  addGameToFavorites,
  excludedPrewagerGamesList,
  getFavoriteGames,
  getFeedCategories,
  getFeedCategory,
  getFeedGames,
  getFeedProvider,
  getFeedProviders,
  getGamesFeed,
  getGamesList,
  getInnerGamesIds,
  getLiveRtpGames,
  getProviders,
  getRecentlyPlayedGames,
  getRecommendedGames,
  getWagerSlotGames,
  removeGameFromFavorites
} from '../hooks/api';
import { CasinoTypeUnion, PlatformType } from '../types';
import { ISingleProviderResponseBase } from '../types/api';
import {
  getGamesStatePreparator,
  getPragmaticInnerGamesIdsStatePreparator,
  plexGetCategoriesStatePreparator,
  plexGetProvidersStatePreparator,
  plexSetGamesStateFormatter,
  plexSetSingleFeedCategoryStatePreparator,
  plexSetSingleLobbyCategoryStatePreparator,
  setFavoriteListStatePreparator,
  setGamesLiveDataStatePreparator,
  setPragmaticGamesLiveDataStatePreparator
} from './actionsFormatters';
import { gameStoreInitialState } from './constants';
import {
  formatProvider,
  normalizeGameFromCms,
  normalizeLiveRtpGames,
  normalizePlexCategories,
  normalizePlexGames,
  normalizePlexSingleProviderResponse,
  normalizeProviderFromPlex
} from './normalize';
import { useGamesStore } from './store';
import {
  ActionsCreatorParams,
  GamesStorageType,
  IPragmaticLiveData,
  LiveDataGamesArr,
  PlexSetGamesArgs,
  PlexSetSingleFeedCategoryArgs,
  PlexSetSingleLobbyCategoryArgs,
  SetFavoriteListArgs
} from './types';

interface IPlexCommonArgs {
  lang: string;
  feedName: string;
  platform: PlatformType;
  filter: {
    offset?: number;
    limit: number;
    launchId?: (number | string)[];
    id?: string[];
  };
}

interface IPlexGetCategoriesProvidersCommonArgs extends IPlexCommonArgs {
  casinoType: CasinoTypeUnion;
}

interface IPlexGetSingleCategoryArgs
  extends IPlexGetCategoriesProvidersCommonArgs {
  categoryId: string;
  isSetSingleFeedCategory: boolean;
}

interface IPlexGetProvidersGamesArgs
  extends IPlexGetCategoriesProvidersCommonArgs {
  providerId: string;
}

interface IPlexGetGamesArgs extends IPlexCommonArgs {
  dontUseGamesStorage?: boolean;
  isSetProviderData?: boolean;
}

interface IPlexPagination {
  entity: 'category' | 'provider';
  pagination: {
    id?: string;
    gamesIds?: string[];
  };
}

const actionsCreator = ({ setState }: ActionsCreatorParams) => ({
  GET_GAMES: async ({
    disabledProviders,
    feedNames,
    platform,
    userSegments
  }: {
    disabledProviders: string[];
    feedNames: string;
    platform: PlatformType;
    userSegments: string[];
  }) => {
    try {
      const data = await getGamesFeed({ feedNames, platform, userSegments });

      setState((state) =>
        getGamesStatePreparator({
          disabledProviders,
          feedNames,
          platform,
          state,
          data
        })
      );
    } catch {
      console.log('On load casino gamesFeed error');
    }
  },

  SET_IS_FEED_LOADING: (isLoading: boolean, feedName: string) =>
    setState((state) => ({
      ...state,
      feeds: {
        ...state.feeds,
        [feedName]: {
          ...state.feeds[feedName],
          isLoading
        }
      }
    })),

  GET_FAVORITES_LIST: async (entityName: string) => {
    const { data } = await getFavoriteGames(entityName);
    const gamesList = data?.[entityName] ?? [];
    actionsCreator({ setState }).SET_FAVORITES_LIST({
      gamesList,
      entityName
    });

    return gamesList?.map((game) => game.id);
  },

  SET_FAVORITES_LIST: ({
    gamesList,
    entityName,
    isGameDeleted,
    isGameAdded
  }: SetFavoriteListArgs) => {
    setState((state) =>
      setFavoriteListStatePreparator({
        gamesList,
        entityName,
        isGameDeleted,
        isGameAdded,
        state
      })
    );
  },

  ADD_GAME_FAVORITES_LIST: async ({
    id,
    entityName
  }: {
    id: string;
    entityName: string;
  }) => {
    const { ok } = await addGameToFavorites({ id, entityName });

    if (ok) {
      actionsCreator({ setState }).SET_FAVORITES_LIST({
        gamesList: [{ id }],
        entityName,
        isGameAdded: true
      });
    }
  },

  REMOVE_GAME_FAVORITES_LIST: async ({
    id,
    entityName
  }: {
    id: string;
    entityName: string;
  }) => {
    const { ok } = await removeGameFromFavorites({
      id,
      entityName
    });

    if (ok) {
      actionsCreator({ setState }).SET_FAVORITES_LIST({
        gamesList: [{ id }],
        entityName,
        isGameDeleted: true
      });
    }
  },

  SET_JACKPOTS: (payload: GamesStorageType['jackpots']) => {
    setState((state) => {
      const updatedJackpots: GamesStorageType['jackpots'] = {};

      for (const [key, value] of Object.entries(payload)) {
        updatedJackpots[key] = {
          ...state.jackpots[key],
          levels: value.levels
        };
      }

      return { ...state, jackpots: updatedJackpots };
    });
  },

  SET_PROVIDER_JACKPOT_GAMES: ({
    jackpotIdentifier,
    gamesIds
  }: {
    jackpotIdentifier: string;
    gamesIds: string[];
  }) => {
    setState((state) => {
      return {
        ...state,
        jackpots: {
          ...state.jackpots,
          [jackpotIdentifier]: {
            ...state.jackpots?.[jackpotIdentifier],
            gamesIds
          }
        }
      };
    });
  },

  GET_PRAGMATIC_INNER_GAMES_IDS: async (serviceId: number) => {
    // increase limit to 1000 until plex functionality will work
    const { data } = await getInnerGamesIds({
      serviceId,
      limit: 1000
    });

    setState((state) =>
      getPragmaticInnerGamesIdsStatePreparator({ state, data })
    );
  },
  // TODO: after pragmatic live-update will be used for all partners - remove reducer below, use SET_GAMES_LIVE_DATA instead
  // now we still use this pragmatic with sockets on casinoua for example
  SET_PRAGMATIC_GAMES_LIVE_DATA: (data: IPragmaticLiveData[]) => {
    setState((state) =>
      setPragmaticGamesLiveDataStatePreparator({ data, state })
    );
  },
  GET_GAME: async ({
    gameId,
    userSegments,
    platform
  }: {
    gameId: string | number;
    userSegments: string[];
    platform: PlatformType;
  }) => {
    const gamesData = await getGamesList({
      gamesIds: [gameId],
      userSegments,
      platform
    });
    const game = gamesData[0];
    if (!isEmpty(gamesData)) {
      setState((state) => ({
        ...state,
        games: {
          ...state.games,
          data: {
            ...state.games.data,
            [game.gameId]: normalizeGameFromCms(game, undefined, platform)
          }
        }
      }));

      return { success: true };
    }

    return { success: false };
  },

  GET_PROVIDER: async (providerId: string) => {
    const providersData = await getProviders();
    const gameProvider = providersData.find(
      (provider) => provider.providerId === providerId
    );
    if (gameProvider) {
      setState((state) => ({
        ...state,
        providers: {
          ...state.providers,
          data: {
            ...state.providers.data,
            [gameProvider.providerId]: formatProvider(gameProvider)
          }
        }
      }));
    }
  },

  SET_RECENTLY_PLAYED_LIST: ({
    gamesList,
    entityName,
    isLoaded
  }: {
    gamesList: string[];
    entityName: string;
    isLoaded: boolean;
  }) => {
    setState((state) => ({
      ...state,
      recentlyPlayed: {
        ...state.recentlyPlayed,
        [entityName]: {
          ids: gamesList,
          isLoaded
        }
      }
    }));
  },

  GET_RECENTLY_PLAYED_LIST: async (entityName: string) => {
    try {
      const { data } = await getRecentlyPlayedGames(entityName);

      const gamesList = data?.internal_game_ids ?? [];
      actionsCreator({ setState }).SET_RECENTLY_PLAYED_LIST({
        gamesList,
        entityName,
        isLoaded: true
      });

      return gamesList;
    } catch (e) {
      console.error(e);
      actionsCreator({ setState }).SET_RECENTLY_PLAYED_LIST({
        gamesList: [],
        entityName,
        isLoaded: true
      });
      return [];
    }
  },
  RESET_RECENTLY_PLAYED_LIST: async (entityName: string) => {
    actionsCreator({ setState }).SET_RECENTLY_PLAYED_LIST({
      gamesList: [],
      entityName,
      isLoaded: false
    });
  },

  GET_RECOMMENDED_GAMES_LIST: async (entityName: string) => {
    try {
      const { data } = await getRecommendedGames(entityName);
      const gamesList = data?.internal_game_ids;
      setState((state) => ({
        ...state,
        recommendedGames: {
          ...state.recommendedGames,
          [entityName]: {
            ids: gamesList,
            isLoaded: true
          }
        }
      }));

      return gamesList;
    } catch (e) {
      console.error(e);
      setState((state) => ({
        ...state,
        recommendedGames: {
          ...state.recommendedGames,
          [entityName]: {
            ids: [],
            isLoaded: true
          }
        }
      }));

      return [];
    }
  },

  GET_WAGER_SLOT_GAMES: async () => {
    try {
      const { data } = await getWagerSlotGames();

      const gamesList = data?.gameIds;
      setState((state) => ({
        ...state,
        dynamicCategories: {
          ...state.dynamicCategories,
          wagerSlotGames: {
            ids: gamesList,
            isLoaded: true
          }
        }
      }));

      return gamesList;
    } catch (e) {
      console.error(e);
      setState((state) => ({
        ...state,
        dynamicCategories: {
          ...state.dynamicCategories,
          wagerSlotGames: {
            ids: [],
            isLoaded: true
          }
        }
      }));

      return [];
    }
  },

  GET_LIVE_RTP_GAMES: async () => {
    try {
      const { data } = await getLiveRtpGames();
      const normalizedLiveRtpGames = normalizeLiveRtpGames(data);

      setState((state) => ({
        ...state,
        dynamicCategories: {
          ...state.dynamicCategories,
          liveRtpGames: {
            ...normalizedLiveRtpGames,
            isLoaded: true
          }
        }
      }));

      const launchIdsData = {
        cold: data?.cold?.map((game) => game.gameId),
        hot: data?.hot?.map((game) => game.gameId),
        mixed: data?.mixed?.map((game) => game.gameId)
      };
      return launchIdsData;
    } catch (e) {
      console.error(e);

      setState((state) => ({
        ...state,
        dynamicCategories: {
          ...state.dynamicCategories,
          liveRtpGames: {
            hot: [],
            cold: [],
            mixed: [],
            data: {},
            isLoaded: true
          }
        }
      }));

      return {
        cold: [],
        hot: [],
        mixed: []
      };
    }
  },

  FETCH_WAGER_EXCLUDED_GAME_LIST: async () => {
    let isBonusExists = false;

    const { data: activeBonusData } = await getBsActiveBonus();
    if (isEmpty(activeBonusData)) {
      const { response: wagerBonusesData } = await getCrmWagerBonuses();
      if (!isEmpty(wagerBonusesData)) {
        isBonusExists = true;
      }
    } else {
      isBonusExists = true;
    }

    if (isBonusExists) {
      const { data } = await excludedPrewagerGamesList(
        SLUG_PARAMETER.prewagerExclusions
      );
      const { gameIds } = data;
      setState((state) => ({
        ...state,
        excludedActiveBonusGamesList: {
          data: gameIds,
          isAlreadyBeenRequested: true
        }
      }));
    }
  },

  PLEX_GET_CATEGORIES: async ({
    lang,
    feedName,
    platform,
    filter,
    casinoType
  }: IPlexGetCategoriesProvidersCommonArgs) => {
    try {
      const data = await getFeedCategories({
        platform,
        lang,
        feedName,
        filter
      });

      const normalizedCategories = normalizePlexCategories(data);

      setState((state) =>
        plexGetCategoriesStatePreparator({
          normalizedCategories,
          state,
          casinoType,
          feedName
        })
      );
    } catch (e) {
      console.error(e);
    }
  },

  SET_IS_PLEX_CATEGORIES_LOADING: (
    isLoading: boolean,
    casinoType: CasinoTypeUnion
  ) =>
    setState((state) => ({
      ...state,
      lobbies: {
        ...state.lobbies,
        [casinoType]: {
          ...state.lobbies[casinoType],
          isCategoriesLoading: isLoading
        }
      }
    })),

  PLEX_GET_PROVIDERS: async ({
    lang,
    feedName,
    platform,
    filter,
    casinoType
  }: IPlexGetCategoriesProvidersCommonArgs) => {
    try {
      const data = await getFeedProviders({
        platform,
        lang,
        feedName,
        filter
      });

      setState((state) =>
        plexGetProvidersStatePreparator({ data, state, casinoType })
      );
    } catch (e) {
      console.error(e);
    }
  },

  SET_IS_PLEX_PROVIDERS_LOADING: (
    isLoading: boolean,
    casinoType: CasinoTypeUnion
  ) =>
    setState((state) => ({
      ...state,
      lobbies: {
        ...state.lobbies,
        [casinoType]: {
          ...state.lobbies[casinoType],
          isProvidersLoading: isLoading
        }
      }
    })),

  PLEX_SET_SINGLE_LOBBY_CATEGORY: ({
    data,
    casinoType
  }: PlexSetSingleLobbyCategoryArgs) => {
    if (!casinoType) {
      return;
    }

    setState((state) =>
      plexSetSingleLobbyCategoryStatePreparator({ state, data, casinoType })
    );
  },

  PLEX_SET_SINGLE_FEED_CATEGORY: ({
    data,
    feedName
  }: PlexSetSingleFeedCategoryArgs) => {
    if (!feedName) {
      return;
    }

    setState((state) =>
      plexSetSingleFeedCategoryStatePreparator({ state, data, feedName })
    );
  },

  PLEX_SET_GAMES: ({ games, launchIdToGameId, ids }: PlexSetGamesArgs) =>
    setState((state) =>
      plexSetGamesStateFormatter({ games, launchIdToGameId, ids, state })
    ),

  PLEX_GET_CATEGORY_GAMES: async ({
    lang,
    feedName,
    platform,
    filter,
    categoryId,
    casinoType,
    isSetSingleFeedCategory = false
  }: IPlexGetSingleCategoryArgs) => {
    try {
      const data = await getFeedCategory({
        platform,
        lang,
        feedName,
        categoryId,
        filter
      });

      if (isEmpty(data)) {
        return [];
      }
      // TODO: also instant games here
      const {
        data: games,
        ids: gamesIds,
        launchIdToGameId
      } = normalizePlexGames(data.games);

      actionsCreator({ setState }).PLEX_SET_GAMES({
        games,
        launchIdToGameId,
        ids: gamesIds
      });

      if (casinoType) {
        actionsCreator({ setState }).PLEX_SET_SINGLE_LOBBY_CATEGORY({
          casinoType,
          data
        });
      }

      if (isSetSingleFeedCategory) {
        actionsCreator({ setState }).PLEX_SET_SINGLE_FEED_CATEGORY({
          feedName,
          data
        });
      }

      return gamesIds;
    } catch (e) {
      console.error(e);

      return [];
    }
  },

  PLEX_GET_PROVIDER_GAMES: async ({
    lang,
    feedName,
    platform,
    filter,
    providerId
  }: IPlexGetProvidersGamesArgs) => {
    try {
      const data = await getFeedProvider({
        platform,
        lang,
        feedName,
        providerId,
        filter
      });
      if (!isEmpty(data)) {
        const normalizedData = normalizePlexSingleProviderResponse(data);

        const {
          data: games,
          ids: gamesIds,
          launchIdToGameId
        } = normalizePlexGames(normalizedData.games);

        actionsCreator({ setState }).PLEX_SET_GAMES({
          games,
          launchIdToGameId,
          ids: gamesIds
        });

        return gamesIds;
      }
      return [];
    } catch (e) {
      console.error(e);

      return [];
    }
  },
  PLEX_SET_PROVIDER: ({
    provider
  }: {
    provider: ISingleProviderResponseBase;
  }) => {
    const normalizedPlexProvider = normalizeProviderFromPlex(provider);
    setState((state) => ({
      ...state,
      providers: {
        ...state.providers,
        data: {
          ...state.providers.data,
          [normalizedPlexProvider.providerId]: normalizedPlexProvider
        }
      }
    }));
  },

  PLEX_GET_GAMES: async ({
    lang,
    feedName,
    platform,
    filter,
    dontUseGamesStorage,
    isSetProviderData = false
  }: IPlexGetGamesArgs) => {
    const res = await getFeedGames({
      platform,
      lang,
      feedName,
      filter
    });
    const dataGames = 'games' in res ? res?.games : res; // requests with feed and without it have different responses
    const dataProviders = 'providers' in res ? res?.providers || [] : [];

    const normalized = normalizePlexGames(dataGames);
    const allRequestedLaunchIds =
      (dontUseGamesStorage ? [] : filter.launchId) || [];
    const allRequestedIds = (dontUseGamesStorage ? [] : filter.id) || [];

    const normalizedPrepared = {
      ...normalized,
      launchIdToGameId: {
        ...normalized.launchIdToGameId,
        ...allRequestedLaunchIds.reduce(
          // checking if requested game has a response, if yes - works normally, if no - set [launchId]:null to obj
          // launchId: null means that launchId was requested, but plex-api filtered out a game with this launchId
          (acc, launchId) => ({
            ...acc,
            [launchId]: normalized.launchIdToGameId[launchId] || null
          }),
          {}
        )
      },
      ids: [...new Set([...normalized.ids, ...allRequestedIds])]
    };

    const {
      data: games,
      ids: gamesIds = [],
      launchIdToGameId
    } = normalizedPrepared;

    actionsCreator({ setState }).PLEX_SET_GAMES({
      games,
      launchIdToGameId,
      ids: gamesIds
    });

    // used for single game data load only (useLoadCasinoSingleGameDataOld hook)
    if (isSetProviderData && dataProviders?.length === 1) {
      actionsCreator({ setState }).PLEX_SET_PROVIDER({
        provider: dataProviders[0]
      });
    }
    if (!isEmpty(dataGames)) {
      return {
        success: true,
        gamesIds,
        newLaunchIdToGameId: launchIdToGameId,
        loadedGamesQty: dataGames.length,
        games
      };
    }

    return {
      success: false,
      gamesIds,
      newLaunchIdToGameId: launchIdToGameId,
      loadedGamesQty: dataGames?.length || 0,
      games
    };
  },

  PLEX_SET_PAGINATION: ({ entity, pagination }: IPlexPagination) => {
    setState((state) => ({
      ...state,
      pagination: {
        ...state.pagination,
        [entity]: pagination
      }
    }));
  },

  RESET_LANG_PLATFORM_RELATED_DATA: () => {
    setState((state) => ({
      ...state,
      lobbies: gameStoreInitialState.lobbies,
      pagination: gameStoreInitialState.pagination,
      games: gameStoreInitialState.games,
      providers: gameStoreInitialState.providers,
      feeds: gameStoreInitialState.feeds
    }));
  },

  SET_GAMES_LIVE_DATA: (liveDataGamesArr: LiveDataGamesArr = []) => {
    setState((state) =>
      setGamesLiveDataStatePreparator({ liveDataGamesArr, state })
    );
  }
});

export const gamesStoreActions = actionsCreator(useGamesStore);
